import {HttpErrorResponse} from '@angular/common/http';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const firstLine = (value: string): string => value.split(/\r?\n/)[0] ?? '';

const truncate = (value: string, maxLen: number): string => {
  if (value.length <= maxLen) return value;
  return value.slice(0, Math.max(0, maxLen - 1)).trimEnd() + '…';
};

const tryParseJsonFromString = (value: string): unknown | null => {
  const trimmed = value.trim();
  const firstBrace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (firstBrace < 0 || lastBrace <= firstBrace) return null;

  const candidate = trimmed.slice(firstBrace, lastBrace + 1);
  try {
    return JSON.parse(candidate);
  } catch {
    return null;
  }
};

const extractMessageFromObject = (obj: Record<string, unknown>): string | null => {
  const candidates = [obj['message'], obj['error'], obj['detail'], obj['title']];
  const candidate = candidates.find(isNonEmptyString);
  const message = candidate ? candidate.trim() : null;

  // Many services return `{ message: "Internal server error", stack: "Error: ..." }`.
  // Prefer the first line of stack when the message is unhelpfully generic.
  if (message && message.toLowerCase() === 'internal server error') {
    const stack = obj['stack'];
    if (isNonEmptyString(stack)) {
      const line = firstLine(stack.trim());
      return line.length ? line : message;
    }
  }

  if (message) return message;

  const stack = obj['stack'];
  if (isNonEmptyString(stack)) return stack.trim();

  return null;
};

const extractMessageFromBody = (body: unknown): string | null => {
  if (!body) return null;

  if (isNonEmptyString(body)) {
    const parsed = tryParseJsonFromString(body);
    const parsedMsg = parsed ? extractMessageFromBody(parsed) : null;
    if (parsedMsg) return parsedMsg;
    return body.trim();
  }

  if (typeof body === 'object') {
    return extractMessageFromObject(body as Record<string, unknown>);
  }

  return null;
};

export const formatHttpErrorMessage = (err: unknown, fallback: string): string => {
  const fallbackClean = (fallback || '').trim() || 'Request failed';

  if (err instanceof HttpErrorResponse) {
    const extracted = extractMessageFromBody(err.error);
    const msg = (extracted || '').trim() || fallbackClean;
    const line = firstLine(msg).trim() || fallbackClean;
    const withStatus = err.status ? `${line} (HTTP ${err.status})` : line;
    return truncate(withStatus, 240);
  }

  if (isNonEmptyString(err)) {
    const line = firstLine(err.trim()).trim();
    return truncate(line || fallbackClean, 240);
  }

  if (err && typeof err === 'object') {
    const maybeMsg = (err as {message?: unknown}).message;
    if (isNonEmptyString(maybeMsg)) {
      const line = firstLine(maybeMsg.trim()).trim();
      return truncate(line || fallbackClean, 240);
    }
  }

  return truncate(fallbackClean, 240);
};

